import { ListItemButton, ListItemIcon } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteForeverTwoTone';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { useAppDispatch } from '@reduxStore';
import { useDeleteLoadTenderMutation } from '@api/Trinium/Endpoints/EDI/slice';
import { useMemo } from 'react';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';
import { queueModal } from '@components/Modals/modalSlice';
import { queueAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import DisabledTooltip from '@components/DisabledTooltip';
import { emptyRowSet } from '../../../types';
import { setEdiLoading, clearEdiLoading } from '../../ediLoadTenderSlice';

export default function DeleteLoadTenderListItem() {
	const { number: terminalNumber } = useCurrentTerminal();
	const { data: user } = useFetchMyProfileQuery(undefined);
	const gridApi = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApi, gridRowSelectionIdsSelector);
	const appDispatch = useAppDispatch();

	const [delEdis] = useDeleteLoadTenderMutation();

	const isDisabled = useMemo(() => {
		return !user?.canDeleteLoadTenders || selectedRowIds.size < 1;
	}, [selectedRowIds, user]);

	const title = !user?.canDeleteLoadTenders
		? 'You do not have permission for this action.'
		: selectedRowIds.size < 1
			? 'This action requires at least one row to be selected.'
			: undefined;

	const handleBtnClick = () => {
		const ids = Array.from(selectedRowIds.keys()).map((k) => Number(k));
		const prompt =
			ids.length > 1
				? `Are you sure you want to delete the ${String(ids.length)} selected EDI transactions?`
				: 'Are you sure you want to delete the selected EDI transaction?';

		appDispatch(
			queueModal({
				Component: 'confirmPopup',
				Props: {
					prompt,
					confirmedAction: () => {
						appDispatch(setEdiLoading(ids));
						void Promise.allSettled(ids.map((id) => delEdis({ terminalNumber, id }).unwrap())).then((results) => {
							appDispatch(clearEdiLoading(ids));
							gridApi.current.setRowSelectionModel(emptyRowSet);

							const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
							if (failures.length > 0) {
								appDispatch(
									queueAnnouncement({
										severity: 'error',
										title:
											failures.length === ids.length
												? getApiErrorMessage(failures[0].reason)
												: `${String(failures.length)} of ${String(ids.length)} deletions failed: ${getApiErrorMessage(failures[0].reason)}`,
										clearable: true
									})
								);
							}
						});
					}
				}
			})
		);
	};

	return (
		<DisabledTooltip
			message={title}
			placement='right'
		>
			<ListItemButton
				disabled={isDisabled}
				title={title}
				onClick={handleBtnClick}
			>
				<ListItemIcon sx={{ minWidth: '24px' }}>
					<DeleteIcon
						fontSize='small'
						color='error'
					/>
				</ListItemIcon>
				Delete
			</ListItemButton>
		</DisabledTooltip>
	);
}
